import { useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Avviso, Caselle, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Riquadro } from '../../componenti/base.tsx'
import { IconaLucchetto } from '../../componenti/Icone.tsx'
import { ChatRiservata } from './ChatRiservata.tsx'

type NotaRiservata = {
  id: string; temi: string[]; testo: string
  autore: { nome: string; cognome: string }; creata_il: string
}

export function AreaRiservata({ personaId, puoScrivere }: { personaId: string; puoScrivere: boolean }) {
  const { tassonomia } = useSessione()
  const [motivo, setMotivo] = useState('')
  const [note, setNote] = useState<NotaRiservata[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [nuovaNota, setNuovaNota] = useState('')
  const [temi, setTemi] = useState<string[]>([])
  const [inCorso, setInCorso] = useState(false)

  const temiDisponibili = (tassonomia?.temi ?? []).filter((t) => t.attivo)
  const nomeTema = (codice: string) => temiDisponibili.find((t) => t.codice === codice)?.nome ?? codice

  async function apri() {
    setErrore(null)
    try { setNote(await api.get<NotaRiservata[]>(`/persone/${personaId}/riservata`, { motivo })) }
    catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
  }

  async function salva() {
    setInCorso(true); setErrore(null)
    try {
      await api.post(`/persone/${personaId}/riservata`, { temi, testo: nuovaNota })
      setNuovaNota(''); setTemi([])
      if (note) await apri()
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  return (
    <>
      <Avviso tipo="attenzione">
        <strong>Quest’area contiene le informazioni più delicate.</strong>{' '}
        Ogni apertura è registrata con la motivazione indicata e resta visibile alla direzione
        e al Referente privacy.
      </Avviso>

      {/* 480: la chat riservata del messenger. Il riquadro si mostra solo a chi l'API lo concede. */}
      <ChatRiservata personaId={personaId} />

      {!note && (
        <Riquadro>
          <Campo etichetta="Motivo dell’apertura" valore={motivo} cambia={setMotivo}
                 segnaposto="es. preparazione del colloquio del 15 settembre" obbligatorio
                 aiuto="Almeno cinque caratteri. La motivazione è conservata cifrata." />
          <Bottone variante="primario" icona={<IconaLucchetto />}
                   onClick={() => void apri()} disabled={motivo.trim().length < 5}>
            Apri l’area riservata
          </Bottone>
        </Riquadro>
      )}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {note && (note.length === 0
        ? <Vuoto testo="Nessuna nota riservata." />
        : note.map((n) => (
            <Riquadro key={n.id}
              titolo={<span className="riga-etichette">
                {n.temi.map((t) => <Etichetta key={t} tono="riservato">{nomeTema(t)}</Etichetta>)}
              </span>}
              azioni={<span className="piccolo tenue">
                {n.autore.nome} {n.autore.cognome} · {new Date(n.creata_il).toLocaleString('it-IT')}
              </span>}
            >
              <div className="testo-preformattato">{n.testo}</div>
            </Riquadro>
          )))}

      {puoScrivere && (
        <Riquadro titolo="Nuova nota riservata">
          <fieldset className="gruppo-caselle">
            <legend className="obbligatorio">Temi</legend>
            <Caselle voci={temiDisponibili} scelte={temi} cambia={setTemi} riservate />
            <p className="campo-aiuto">
              Uno o più temi dall’elenco: dicono di che cosa tratta la nota e servono a ritrovarla. Non
              ne limitano la lettura: tutte le note le legge chiunque possa aprire l’area riservata di
              questa scheda.
            </p>
          </fieldset>
          <AreaTesto etichetta="Testo della nota" valore={nuovaNota} cambia={setNuovaNota}
                     righe={5} obbligatorio />
          <Bottone variante="primario" inCorso={inCorso}
                   onClick={() => void salva()}
                   disabled={!nuovaNota.trim() || temi.length === 0}>
            Salva la nota
          </Bottone>
        </Riquadro>
      )}
    </>
  )
}
